import React from 'react';
import Button from '@ajat/design-system/Button';
import { UserRound, ArrowRight, Plus, Trash2, Settings } from 'lucide-react';
import { ComponentPage, DemoCard, DemoRow, Demo } from '../shared';

const VARIANTS = ['main', 'alternative', 'danger', 'outline', 'black', 'gray', 'text', 'hero'];

export const meta = {
  component: Button,
  title: 'Button',
  group: 'Actions',
  importPath: "_designSystem/Button",
  figmaNode: '3617:488',
  description:
    'Three sizes (52 / 44 / 36px) across ten colour variants — including the brand-skinned hero CTA — with optional leading and trailing icons, an icon-only square form, full-width stretching, and loading / success / disabled states.',
  props: [
    ['children', 'node', '—', 'Button content'],
    ['size', "'primary' | 'secondary' | 'tertiary'", "'primary'", 'Size variant — primary 52px, secondary 44px, tertiary 36px'],
    ['variant', "'main' | 'alternative' | 'danger' | 'outline' | 'black' | 'white' | 'gray' | 'text' | 'text-danger' | 'hero'", "'main'", 'Colour variant — `hero` is the signature CTA: a 52px gradient pill with an animated rim, skinned per brand through the `--button-hero-*` tokens (OVER/LL "Nästa", the landing hero CTA, the cart checkout)'],
    ['iconLeft', 'node', 'null', 'Icon rendered before the label'],
    ['iconRight', 'node', 'null', 'Icon rendered after the label'],
    ['iconOnly', 'bool', 'false', 'Square icon-only button — pass the icon as iconLeft'],
    ['loading', 'bool', 'false', 'Swaps the label for a spinner and blocks clicks'],
    ['success', 'bool', 'false', 'Brief success checkmark state'],
    ['disabled', 'bool', 'false', 'Disables the button'],
    ['fullWidth', 'bool', 'false', 'Stretches the button to its container — use it instead of a consumer `width: 100%` rule'],
    ['className', 'string', "''", 'Additional CSS class'],
    ['type', "'button' | 'submit' | 'reset'", "'button'", 'Native button type attribute'],
    ['href', 'string', '—', 'Renders a real link (<a>) with the button look — for CTAs that lead to a URL'],
    ['onClick', 'func', '—', 'Click handler'],
  ],
  usage: [
    {
      title: 'Basic',
      code: `import Button from '@ajat/design-system/Button';

<Button size="primary" variant="main" onClick={handleSave}>
  Save changes
</Button>`,
    },
    {
      title: 'With icons',
      code: `import { Plus, ArrowRight } from 'lucide-react';

// Icons are always 20px in the DS
<Button iconLeft={<Plus size={20} />}>Add new</Button>
<Button iconRight={<ArrowRight size={20} />}>Continue</Button>

// Icon-only — the icon goes in iconLeft
<Button size="tertiary" iconOnly iconLeft={<Settings size={20} />} />`,
    },
    {
      title: 'Async submit',
      code: `const [saving, setSaving] = useState(false);

<Button
  variant="main"
  loading={saving}
  disabled={!isValid}
  onClick={async () => {
    setSaving(true);
    await save();
    setSaving(false);
  }}
>
  Submit
</Button>`,
    },
  ],
};

export default function ButtonPage() {
  return (
    <ComponentPage meta={meta}>
      <DemoCard title="Sizes" hint="primary 52px · secondary 44px · tertiary 36px">
        <DemoRow align="center">
          <Demo caption="PRIMARY">
            <Button size="primary" iconLeft={<UserRound size={20} />} iconRight={<ArrowRight size={20} />}>
              Primary
            </Button>
          </Demo>
          <Demo caption="SECONDARY">
            <Button size="secondary" iconLeft={<UserRound size={20} />} iconRight={<ArrowRight size={20} />}>
              Secondary
            </Button>
          </Demo>
          <Demo caption="TERTIARY">
            <Button size="tertiary" iconLeft={<UserRound size={20} />} iconRight={<ArrowRight size={20} />}>
              Tertiary
            </Button>
          </Demo>
        </DemoRow>
      </DemoCard>

      <DemoCard title="Colour variants">
        <DemoRow align="center">
          <Button variant="main">Main</Button>
          <Button variant="alternative">Alternative</Button>
          <Button variant="danger">Danger</Button>
          <Button variant="outline">Outline</Button>
          <Button variant="black">Black</Button>
          <Button variant="white">White</Button>
          <Button variant="gray">Gray</Button>
          <Button variant="text">Text</Button>
          <Button variant="text-danger">Text Danger</Button>
        </DemoRow>
      </DemoCard>

      <DemoCard title="Hero" hint="the signature CTA — brand tokens --button-hero-* skin the fill; switch the brand in the topbar">
        <DemoRow align="center">
          <Button variant="hero" iconRight={<ArrowRight size={20} />}>Nästa</Button>
          <Button variant="hero" size="secondary" iconRight={<ArrowRight size={20} />}>Till kassan</Button>
          <Button variant="hero" disabled>Disabled</Button>
        </DemoRow>
      </DemoCard>

      <DemoCard title="Full width" hint="fullWidth — the column decides the width">
        <div style={{ maxWidth: 360 }}>
          <Button fullWidth iconRight={<ArrowRight size={20} />}>Go to checkout</Button>
        </div>
      </DemoCard>

      <DemoCard title="With icons">
        <DemoRow align="center">
          <Button iconLeft={<Plus size={20} />}>Add New</Button>
          <Button iconRight={<ArrowRight size={20} />}>Continue</Button>
          <Button variant="danger" iconLeft={<Trash2 size={20} />}>Delete</Button>
          <Button variant="outline" iconLeft={<Settings size={20} />}>Settings</Button>
        </DemoRow>
      </DemoCard>

      <DemoCard title="Icon only">
        <DemoRow align="center">
          <Button size="primary" iconOnly iconLeft={<Plus size={20} />} />
          <Button size="secondary" iconOnly iconLeft={<Settings size={20} />} />
          <Button size="tertiary" iconOnly iconLeft={<ArrowRight size={20} />} />
          <Button variant="outline" iconOnly iconLeft={<UserRound size={20} />} />
          <Button variant="danger" iconOnly iconLeft={<Trash2 size={20} />} />
        </DemoRow>
      </DemoCard>

      <DemoCard title="States">
        <DemoRow align="center">
          <Demo caption="DEFAULT"><Button>Default</Button></Demo>
          <Demo caption="LOADING"><Button loading>Loading</Button></Demo>
          <Demo caption="SUCCESS"><Button success>Saved</Button></Demo>
          <Demo caption="DISABLED"><Button disabled>Disabled</Button></Demo>
          <Demo caption="OUTLINE LOADING"><Button variant="outline" loading>Loading</Button></Demo>
          <Demo caption="DANGER DISABLED"><Button variant="danger" disabled>Disabled</Button></Demo>
        </DemoRow>
      </DemoCard>

      <DemoCard title="Full matrix" hint="size × variant">
        <div style={{ overflowX: 'auto' }}>
          <table className="ds-page__table">
            <thead>
              <tr>
                <th className="typo-label">Variant</th>
                <th className="typo-label">Primary</th>
                <th className="typo-label">Secondary</th>
                <th className="typo-label">Tertiary</th>
              </tr>
            </thead>
            <tbody>
              {VARIANTS.map((variant) => (
                <tr key={variant}>
                  <td className="typo-body-sm ds-page__table-type" style={{ textTransform: 'capitalize' }}>
                    {variant}
                  </td>
                  <td><Button size="primary" variant={variant}>{variant}</Button></td>
                  <td><Button size="secondary" variant={variant}>{variant}</Button></td>
                  <td><Button size="tertiary" variant={variant}>{variant}</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DemoCard>
    </ComponentPage>
  );
}
